import { FilePdfOutlined, PictureOutlined } from "@ant-design/icons";
import { Button, DatePicker, Form, Input, Row, Select, Upload, Switch } from "antd";
import PropTypes from "prop-types";
import dayjs from "dayjs";
import { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import PhoneNumberInput from "components/shared-components/PhoneNumberInput";
import { buildPhoneRule } from "utils/phone";

const { TextArea } = Input;

export default function InstructorForm({
  form,
  onSubmit,
  schools,
  isEditing = false,
}) {
  const { loading } = useSelector((state) => state?.instructors);
  const { t } = useTranslation();

  const normFile = (e) => {
    if (Array?.isArray(e)) {
      return e;
    }
    return e && e?.fileList;
  };

  const generateRandomPassword = () => {
    const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789@$!%*?&";
    let password = "";
    for (let i = 0; i < 12; i += 1) {
      password += chars?.charAt(Math?.floor(Math?.random() * chars?.length));
    }
    return password;
  };

  const handleGeneratePassword = useCallback(() => {
    const password = generateRandomPassword();
    form?.setFieldsValue({
      password,
      confirmPassword: password,
    });
  }, [form]);

  useEffect(() => {
    if (!isEditing) {
      handleGeneratePassword();
    }
  }, [isEditing, handleGeneratePassword]);

  const validateIdentityCard = (_, value) => {
    if (!value || /^\d+$/.test(value)) {
      return Promise.resolve();
    }
    return Promise.reject(
      t("instructors.crud.form.error.label.invalid.idCardNumber", {
        defaultValue: "ID card number must contain digits only",
      })
    );
  };

  return (
    <Form
      layout="vertical"
      name="instructor_form"
      id="instructor_form"
      form={form}
      onFinish={onSubmit}
      disabled={loading}
      style={{
        maxWidth: 600,
        margin: "0 auto",
      }}
    >
      <Form.Item
        name="image"
        valuePropName="fileList"
        getValueFromEvent={normFile}
        style={{ textAlign: "center" }}
      >
        <Upload
          name="instructorImg" // Modification pour correspondre au nom attendu par le backend
          listType="picture-circle"
          maxCount={1}
          progress={{ strokeWidth: 2, showInfo: false }}
          beforeUpload={() => false}
        >
          <Row span={24} align="middle" justify="center">
            <PictureOutlined style={{ fontSize: "24px" }} />
            {t("instructors.crud.form.button.upload.picture")}
          </Row>
        </Upload>
      </Form.Item>
      <Form.Item
        label={t("instructors.crud.form.label.school")}
        name="schoolId"
        rules={[
          {
            required: true,
            message: t("instructors.crud.form.error.label.required.school"),
          },
        ]}
      >
        <Select>
          {schools?.map((school) => (
            <Select.Option key={school?._id} value={school?._id}>
              {school?.name}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>
      <Form.Item
        label={t("instructors.crud.form.label.name")}
        name="fullName"
        rules={[
          {
            required: true,
            message: t("instructors.crud.form.error.label.required.name"),
          },
        ]}
      >
        <Input />
      </Form.Item>
      <Form.Item
        label={t("instructors.crud.form.label.title", { defaultValue: "Title" })}
        name="title"
      >
        <Input />
      </Form.Item>
      <Form.Item
        label={t("instructors.crud.form.label.speciality", {
          defaultValue: "Speciality",
        })}
        name="speciality"
      >
        <Input />
      </Form.Item>
      <Form.Item
        label={t("instructors.crud.form.label.birthDate")}
        name="birthDate"
        rules={[
          {
            validator: (_, value) => {
              if (!value || dayjs()?.diff(value, "years") >= 18) {
                return Promise?.resolve();
              }
              return Promise?.reject(
                t("instructors.crud.form.error.label.invalid.birthDate")
              );
            },
          },
        ]}
      >
        <DatePicker style={{ width: "100%" }} allowClear format="YYYY-MM-DD" />
      </Form.Item>
      <Form.Item
        label={t("instructors.crud.form.label.email")}
        name="email"
        rules={[
          {
            required: true,
            message: t("instructors.crud.form.error.label.required.email"),
            type: "email",
          },
        ]}
      >
        <Input />
      </Form.Item>
      <Form.Item
        label={t("instructors.crud.form.label.phone")}
        name="phoneNumber"
        rules={[
          {
            required: true,
            message: t("instructors.crud.form.error.label.required.phone"),
          },
          buildPhoneRule(t, false),
        ]}
      >
        <PhoneNumberInput />
      </Form.Item>
      <Form.Item
        label={t("instructors.crud.form.label.wilaya")}
        name="wilaya"
      >
        <Input />
      </Form.Item>
      <Form.Item
        label={t("instructors.crud.form.label.idCardNumber")}
        name="identityCardNumber"
        rules={[
          {
            validator: validateIdentityCard,
          },
        ]}
      >
        <Input inputMode="numeric" />
      </Form.Item>
      <Form.Item
        label={t("instructors.crud.form.label.profile", { defaultValue: "Profile" })}
        name="profile"
      >
        <TextArea rows={3} showCount maxLength={500} />
      </Form.Item>
      <Form.Item
        label={t("instructors.crud.form.label.shortDescription", {
          defaultValue: "Short description",
        })}
        name="shortDescription"
      >
        <TextArea rows={3} showCount maxLength={300} />
      </Form.Item>
      {!isEditing && (
        <>
          <Form.Item
            label={t("instructors.crud.form.label.password")}
            name="password"
            hasFeedback
            rules={[
              {
                required: true,
                message: t(
                  "instructors.crud.form.error.label.required.password"
                ),
              },
              {
                min: 6,
                message: t(
                  "instructors.crud.form.error.label.minLength.password"
                ),
              },
            ]}
          >
            <Input.Password
              addonAfter={
                <Button
                  type="link"
                  size="small"
                  onMouseDown={(event) => event?.preventDefault()}
                  onClick={handleGeneratePassword}
                >
                  {t("instructors.form.button.generatePassword", {
                    defaultValue: "Générer",
                  })}
                </Button>
              }
            />
          </Form.Item>

          <Form.Item
            label={t("instructors.crud.form.label.confirmPassword")}
            name="confirmPassword"
            dependencies={["password"]}
            hasFeedback
            rules={[
              {
                required: true,
                message: t(
                  "instructors.crud.form.error.label.required.confirmPassword"
                ),
              },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue("password") === value) {
                    return Promise?.resolve();
                  }
                  return Promise?.reject(
                    t("instructors.crud.form.error.password.notMatch")
                  );
                },
              }),
            ]}
          >
            <Input.Password />
          </Form.Item>
        </>
      )}
      <Form.Item
        name="cv"
        valuePropName="fileList"
        getValueFromEvent={normFile}
        style={{ textAlign: "center" }}
      >
        <Upload
          name="cv"
          listType="text"
          maxCount={1}
          progress={{ strokeWidth: 2, showInfo: false }}
          beforeUpload={() => false}
        >
          <Button
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            icon={
              <FilePdfOutlined
                style={{
                  fontSize: "24px",
                }}
              />
            }
            type="dashed"
          >
            {t("instructors.crud.form.button.upload.cv")}
          </Button>
        </Upload>
      </Form.Item>
      <Form.Item
        label={t("instructors.crud.form.label.featured", { defaultValue: "Featured" })}
        name="isFeutured"
        valuePropName="checked"   
      >
        <Switch checkedChildren="Yes" unCheckedChildren="No" />
      </Form.Item>
    </Form>
  );
}

InstructorForm.propTypes = {
  form: PropTypes.shape({
    setFieldsValue: PropTypes.func,
  }).isRequired,
  onSubmit: PropTypes.func.isRequired,
  schools: PropTypes.arrayOf(
    PropTypes.shape({
      _id: PropTypes.string,
      name: PropTypes.string,
    })
  ),
  isEditing: PropTypes.bool,
};

InstructorForm.defaultProps = {
  schools: [],
  isEditing: false,
};
